/**
 * Blog content loader. Posts are markdown files in apps/web-client/content/blog/
 * with flat frontmatter:
 *
 *   ---
 *   title: How to find your saved TikToks
 *   description: One-sentence summary used for meta description and cards.
 *   date: 2026-06-12
 *   updated: 2026-06-12        (optional)
 *   author: Andrew
 *   category: guides           (required; slug from content/blog/categories.json)
 *   tags: tiktok, saves        (optional, comma-separated)
 *   ogImage: https://...       (optional; defaults to the shared blog image)
 *   cardImage: /blog/card/...  (optional; lightweight card art for index/related grids)
 *   collection: july-4-2026    (optional; editorial grouping for index rails)
 *   featured: true             (optional; pins the post as the index hero)
 *   stashOverview: false       (optional; keeps save cards inline without a repeated overview grid)
 *   mobileTables: stacked     (optional; labeled comparison rows on narrow screens)
 *   seoTitle: Search title    (optional; preserves a distinct approved article H1)
 *   ---
 *
 * The filename (minus .md) is the slug. Markdown renders client-side via
 * marked; the prerender step bakes the result into static HTML for crawlers.
 * scripts/seo/content.mjs parses the same frontmatter at build time and fails
 * the production build on missing required fields, so runtime can assume
 * mostly-valid input and merely skip + log anything malformed.
 */

import { marked } from "marked";
import { stackBlogTables } from "@/lib/blogTableLayout";
import { attributeStoreUrl } from "@/lib/storeLinks";
import type { FaqEntry } from "@/lib/structuredData";
import CATEGORY_DATA from "../../content/blog/categories.json";

export interface BlogCategory {
  /** URL slug, e.g. "places" → /blog/category/places. */
  slug: string;
  name: string;
  description: string;
}

/** Display order matters: topical categories first, the Guides shelf last. */
export const BLOG_CATEGORIES: ReadonlyArray<BlogCategory> = Object.entries(CATEGORY_DATA).map(
  ([slug, category]) => ({ slug, ...category }),
);

export const getCategoryBySlug = (slug: string): BlogCategory | undefined =>
  BLOG_CATEGORIES.find(category => category.slug === slug);

export interface BlogHeading {
  /** GitHub-style slug, matches the id injected into the rendered heading. */
  id: string;
  text: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  seoTitle?: string;
  description: string;
  /** ISO date (YYYY-MM-DD). */
  date: string;
  updated?: string;
  author: string;
  /** Category slug; guaranteed to exist in BLOG_CATEGORIES. */
  category: string;
  tags: string[];
  ogImage?: string;
  cardImage?: string;
  collection?: string;
  featured: boolean;
  readingMinutes: number;
  html: string;
  blocks: BlogContentBlock[];
  /** H2 headings in document order, for the table of contents. */
  headings: BlogHeading[];
  /** Q&A pairs extracted from the "Common questions" section, if present. */
  faq: FaqEntry[];
}

export interface BlogStashCard {
  id: string;
  title: string;
  description: string;
  sourceLabel?: string;
  platform?: string;
  tags: string[];
}

export interface BlogCollectionCard {
  id: string;
  title: string;
  description: string;
  /** Public stash ids selected by the author for the visual preview. */
  previewStashIds: string[];
}

export interface BlogStashGrid {
  title: string;
  description?: string;
  themes: string[];
  limit?: number;
  showFilters: boolean;
}

export interface BlogLinksBox {
  title?: string;
  html: string;
}

export type BlogContentBlock =
  | { type: "markdown"; html: string }
  | { type: "inline-cta" }
  | { type: "stash-card"; card: BlogStashCard }
  | { type: "stash-card-grid"; cards: BlogStashCard[]; variant?: "overview" }
  | { type: "collection-card"; card: BlogCollectionCard }
  | { type: "stash-grid"; grid: BlogStashGrid }
  | { type: "links-box"; box: BlogLinksBox };

const FRONTMATTER_PATTERN = /^---\n([\s\S]*?)\n---\n?/;
const CUSTOM_BLOCK_PATTERN = /^:::\s*(stash-card|collection-card|stash-grid|links-box)\s*\n([\s\S]*?)^:::\s*$/gm;
const REQUIRED_FIELDS = ["title", "description", "date", "author", "category"] as const;

const parseFrontmatter = (raw: string): { data: Record<string, string>; body: string } => {
  const match = raw.match(FRONTMATTER_PATTERN);
  if (!match) return { data: {}, body: raw };

  const data: Record<string, string> = {};
  match[1].split("\n").forEach(line => {
    const separator = line.indexOf(":");
    if (separator === -1) return;
    const key = line.slice(0, separator).trim();
    const value = line
      .slice(separator + 1)
      .trim()
      .replace(/^["']|["']$/g, "");
    if (key) data[key] = value;
  });

  return { data, body: raw.slice(match[0].length) };
};

const parseKeyValueBlock = (raw: string): Record<string, string> => {
  const data: Record<string, string> = {};
  raw.split("\n").forEach(line => {
    const separator = line.indexOf(":");
    if (separator === -1) return;

    const key = line.slice(0, separator).trim();
    const value = line
      .slice(separator + 1)
      .trim()
      .replace(/^["']|["']$/g, "");
    if (key) data[key] = value;
  });
  return data;
};

const toStashCard = (raw: string): BlogStashCard | null => {
  const data = parseKeyValueBlock(raw);
  if (!data.id || !data.title || !data.description) {
    console.error("[blog] Skipping stash-card block: missing id, title, or description");
    return null;
  }

  return {
    id: data.id,
    title: data.title,
    description: data.description,
    sourceLabel: data.sourceLabel || undefined,
    platform: data.platform || undefined,
    tags: data.tags ? data.tags.split(",").map(tag => tag.trim()).filter(Boolean) : [],
  };
};

const toCollectionCard = (raw: string): BlogCollectionCard | null => {
  const data = parseKeyValueBlock(raw);
  if (!data.id || !data.title || !data.description) {
    console.error("[blog] Skipping collection-card block: missing id, title, or description");
    return null;
  }

  const previewStashIds = [
    ...new Set(
      (data.previewStashes || "")
        .split(",")
        .map(id => id.trim())
        .filter(Boolean),
    ),
  ].slice(0, 3);

  return {
    id: data.id,
    title: data.title,
    description: data.description,
    previewStashIds,
  };
};

const parseOptionalPositiveInteger = (value: string | undefined): number | undefined => {
  if (!value) return undefined;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const parseBoolean = (value: string | undefined, fallback: boolean): boolean => {
  if (!value) return fallback;
  return !["false", "no", "0"].includes(value.trim().toLowerCase());
};

const toStashGrid = (raw: string): BlogStashGrid => {
  const data = parseKeyValueBlock(raw);

  return {
    title: data.title || "Explore real Stasht examples",
    description: data.description || undefined,
    themes: data.themes ? data.themes.split(",").map(theme => theme.trim()).filter(Boolean) : [],
    limit: parseOptionalPositiveInteger(data.limit),
    showFilters: parseBoolean(data.showFilters, true),
  };
};

const HREF_ATTRIBUTE_PATTERN = /href="([^"]+)"/g;

const escapeAttribute = (value: string): string => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");

/**
 * Posts' "About Stasht" sections link the stores as plain markdown. Rather
 * than tagging 25 .md files, campaign attribution is applied to store hrefs
 * at render time — which also bakes the tagged URLs into prerendered HTML.
 * The surface is a coarse "blog" on purpose (per-post granularity is a
 * phase-2 call); non-store hrefs pass through untouched.
 */
const attributeStoreHrefs = (html: string): string =>
  html.replace(HREF_ATTRIBUTE_PATTERN, (match, href: string) => {
    const decoded = decodeEntities(href);
    const attributed = attributeStoreUrl(decoded, "blog");
    return attributed === decoded ? match : `href="${escapeAttribute(attributed)}"`;
  });

const renderMarkdown = (body: string): string =>
  attributeStoreHrefs(marked.parse(body, { gfm: true, async: false }) as string);

/**
 * A links-box renders a resource-list section (e.g. "Official links") as a
 * compact bordered box instead of a wall of bullet links. Body is markdown;
 * an optional leading "title:" line becomes the box heading.
 */
const toLinksBox = (raw: string): BlogLinksBox => {
  const titleMatch = raw.match(/^title:\s*(.+)\s*$/m);
  const body = titleMatch ? raw.replace(titleMatch[0], "") : raw;
  return {
    title: titleMatch?.[1].replace(/^["']|["']$/g, "") || undefined,
    html: renderMarkdown(body.trim()),
  };
};

/**
 * GitHub-style heading slug. Must stay in sync with the anchor convention
 * already used by hand-written "In this guide:" links in existing posts
 * ("Rooftops & Bars" → "rooftops--bars": punctuation stripped, each space
 * becomes a dash, runs are not collapsed).
 */
const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s/g, "-");

const decodeEntities = (text: string): string =>
  text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

/**
 * marked emits bare <h2>/<h3> tags; inject GitHub-style ids so in-page anchors
 * and the table of contents work. The slug counter is shared across all
 * markdown blocks of a post so duplicate headings stay unique document-wide.
 */
const injectHeadingIds = (
  html: string,
  headings: BlogHeading[],
  slugCounts: Map<string, number>,
): string =>
  html.replace(/<h([23])>([\s\S]*?)<\/h\1>/g, (_match, level: string, inner: string) => {
    const text = decodeEntities(inner.replace(/<[^>]*>/g, "")).trim();
    const base = slugifyHeading(text);
    const count = slugCounts.get(base) ?? 0;
    slugCounts.set(base, count + 1);
    const id = count === 0 ? base : `${base}-${count}`;
    if (level === "2") headings.push({ id, text });
    return `<h${level} id="${id}">${inner}</h${level}>`;
  });

const parseContentBlocks = (body: string): BlogContentBlock[] => {
  const blocks: BlogContentBlock[] = [];
  let cursor = 0;

  for (const match of body.matchAll(CUSTOM_BLOCK_PATTERN)) {
    const index = match.index ?? 0;
    const markdown = body.slice(cursor, index).trim();
    if (markdown) {
      blocks.push({ type: "markdown", html: renderMarkdown(markdown) });
    }

    const blockType = match[1];
    const blockBody = match[2];
    if (blockType === "stash-card") {
      const card = toStashCard(blockBody);
      if (card) {
        blocks.push({ type: "stash-card", card });
      }
    } else if (blockType === "collection-card") {
      const card = toCollectionCard(blockBody);
      if (card) {
        blocks.push({ type: "collection-card", card });
      }
    } else if (blockType === "stash-grid") {
      blocks.push({ type: "stash-grid", grid: toStashGrid(blockBody) });
    } else if (blockType === "links-box") {
      blocks.push({ type: "links-box", box: toLinksBox(blockBody) });
    }

    cursor = index + match[0].length;
  }

  const tail = body.slice(cursor).trim();
  if (tail) {
    blocks.push({ type: "markdown", html: renderMarkdown(tail) });
  }

  return blocks;
};

/**
 * A single stash card reads well inline; several in a row stack into a wall.
 * Each contiguous run of 2+ cards becomes one homepage-style grid in place,
 * so cards stay with the section they illustrate; lone cards stay inline.
 * Authoring is unchanged: per-section `:::stash-card` blocks.
 */
const consolidateStashCards = (blocks: BlogContentBlock[]): BlogContentBlock[] => {
  const result: BlogContentBlock[] = [];
  let run: BlogStashCard[] = [];

  const flushRun = () => {
    if (run.length === 1) result.push({ type: "stash-card", card: run[0] });
    if (run.length >= 2) result.push({ type: "stash-card-grid", cards: run });
    run = [];
  };

  for (const block of blocks) {
    if (block.type === "stash-card") {
      run.push(block.card);
      continue;
    }
    flushRun();
    result.push(block);
  }
  flushRun();

  return result;
};

const OVERVIEW_MIN_CARDS = 4;
const OVERVIEW_MIN_LOCATIONS = 2;

/**
 * Posts whose saves are spread through the article also get an overview grid
 * of ALL of them, inserted after the intro (right before the first H2) —
 * a browsable mosaic up top, with the smaller contextual chunks still in
 * place below. Skipped when the cards all sit in one spot (the overview
 * would just duplicate the grid beside it) or there are too few to browse.
 */
const injectSaveOverview = (blocks: BlogContentBlock[]): BlogContentBlock[] => {
  const cardBlocks = blocks.filter(
    (block): block is Extract<BlogContentBlock, { type: "stash-card" | "stash-card-grid" }> =>
      block.type === "stash-card" || block.type === "stash-card-grid",
  );
  const allCards = cardBlocks.flatMap(block => (block.type === "stash-card" ? [block.card] : block.cards));
  if (allCards.length < OVERVIEW_MIN_CARDS || cardBlocks.length < OVERVIEW_MIN_LOCATIONS) return blocks;

  const overview: BlogContentBlock = { type: "stash-card-grid", cards: allCards, variant: "overview" };

  // Insert before the first H2; if the intro and first section share one
  // markdown block, split it at the heading. (Heading ids are injected later,
  // so the tag is still a bare <h2> here.)
  const result: BlogContentBlock[] = [];
  let placed = false;
  for (const block of blocks) {
    if (!placed && block.type === "markdown") {
      const headingIndex = block.html.indexOf("<h2>");
      if (headingIndex > 0) {
        result.push({ type: "markdown", html: block.html.slice(0, headingIndex) });
        result.push(overview);
        result.push({ type: "markdown", html: block.html.slice(headingIndex) });
        placed = true;
        continue;
      }
      if (headingIndex === 0) {
        result.push(overview);
        result.push(block);
        placed = true;
        continue;
      }
    }
    result.push(block);
  }
  if (!placed) result.unshift(overview);

  return result;
};

const INLINE_CTA_BEFORE_H2_NUMBER = 3;

/**
 * Give readers two useful sections before showing the inline Stasht path.
 * Inserting a typed block keeps CTA placement consistent without putting
 * promotional copy into every article's editorial source.
 */
const injectInlineCta = (blocks: BlogContentBlock[]): BlogContentBlock[] => {
  const result: BlogContentBlock[] = [];
  let h2Count = 0;

  for (const block of blocks) {
    if (block.type !== "markdown") {
      result.push(block);
      continue;
    }

    const headingPattern = /<h2>/g;
    let match: RegExpExecArray | null;
    let insertionIndex: number | null = null;

    while ((match = headingPattern.exec(block.html)) !== null) {
      h2Count += 1;
      if (h2Count === INLINE_CTA_BEFORE_H2_NUMBER) {
        insertionIndex = match.index;
        break;
      }
    }

    if (insertionIndex === null) {
      result.push(block);
      continue;
    }

    if (insertionIndex > 0) {
      result.push({ type: "markdown", html: block.html.slice(0, insertionIndex) });
    }
    result.push({ type: "inline-cta" });
    result.push({ type: "markdown", html: block.html.slice(insertionIndex) });
  }

  return result;
};

/**
 * Posts use a conventional FAQ shape: a "## Common questions" section whose
 * questions are standalone bold paragraphs followed by answer paragraphs.
 * Extracting it here lets BlogPostPage emit FAQPage JSON-LD without a second
 * copy of the questions in frontmatter.
 */
const FAQ_HEADING_PATTERN = /^##\s+(?:Common questions|FAQ|Frequently asked questions)\s*$/im;
const BOLD_QUESTION_PATTERN = /^\*\*(.+?)\*\*$/;

const markdownToPlainText = (markdown: string): string =>
  markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^>\s?/gm, "")
    .replace(/\s+/g, " ")
    .trim();

const extractFaqEntries = (body: string): FaqEntry[] => {
  const headingMatch = body.match(FAQ_HEADING_PATTERN);
  if (!headingMatch || headingMatch.index === undefined) return [];

  const afterHeading = body.slice(headingMatch.index + headingMatch[0].length);
  const sectionEnd = afterHeading.search(/^(?:##\s|:::)/m);
  const section = sectionEnd === -1 ? afterHeading : afterHeading.slice(0, sectionEnd);

  const entries: FaqEntry[] = [];
  let question: string | null = null;
  let answerParts: string[] = [];

  const flush = () => {
    if (question && answerParts.length > 0) {
      entries.push({ question, answer: markdownToPlainText(answerParts.join(" ")) });
    }
    answerParts = [];
  };

  // Questions are bold first lines; the answer is the rest of the same
  // paragraph ("**Q?**\nAnswer…") plus any follow-on paragraphs.
  for (const paragraph of section.split(/\n{2,}/).map(part => part.trim()).filter(Boolean)) {
    const [firstLine, ...restLines] = paragraph.split("\n");
    const questionMatch = firstLine.trim().match(BOLD_QUESTION_PATTERN);
    if (questionMatch) {
      flush();
      question = markdownToPlainText(questionMatch[1]);
      if (restLines.length > 0) answerParts.push(restLines.join(" "));
    } else if (question) {
      answerParts.push(paragraph);
    }
  }
  flush();

  return entries;
};

const WORDS_PER_MINUTE = 225;

const toPost = (path: string, raw: string): BlogPost | null => {
  const slug = path.split("/").pop()?.replace(/\.md$/, "") ?? "";
  const { data, body } = parseFrontmatter(raw);

  const missing = REQUIRED_FIELDS.filter(field => !data[field]);
  if (!slug || missing.length > 0) {
    console.error(`[blog] Skipping ${path}: missing frontmatter field(s) ${missing.join(", ")}`);
    return null;
  }
  if (!getCategoryBySlug(data.category)) {
    console.error(`[blog] Skipping ${path}: unknown category "${data.category}"`);
    return null;
  }

  const headings: BlogHeading[] = [];
  const slugCounts = new Map<string, number>();
  const contentBlocks = consolidateStashCards(parseContentBlocks(body));
  const blocksWithOverview = parseBoolean(data.stashOverview, true) ? injectSaveOverview(contentBlocks) : contentBlocks;
  const layoutTables = data.mobileTables === "stacked" ? stackBlogTables : (html: string) => html;
  const blocks = injectInlineCta(blocksWithOverview).map(block =>
    block.type === "markdown"
      ? { ...block, html: layoutTables(injectHeadingIds(block.html, headings, slugCounts)) }
      : block,
  );

  return {
    slug,
    title: data.title,
    seoTitle: data.seoTitle || undefined,
    description: data.description,
    date: data.date,
    updated: data.updated || undefined,
    author: data.author,
    category: data.category,
    tags: data.tags ? data.tags.split(",").map(tag => tag.trim()) : [],
    ogImage: data.ogImage || undefined,
    cardImage: data.cardImage || undefined,
    collection: data.collection || undefined,
    featured: parseBoolean(data.featured, false),
    readingMinutes: Math.max(1, Math.round(body.split(/\s+/).length / WORDS_PER_MINUTE)),
    html: layoutTables(renderMarkdown(body)),
    blocks,
    headings,
    faq: extractFaqEntries(body),
  };
};

const modules = import.meta.glob("../../content/blog/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
}) as Record<string, string>;

const POSTS: ReadonlyArray<BlogPost> = Object.entries(modules)
  .map(([path, raw]) => toPost(path, raw))
  .filter((post): post is BlogPost => post !== null)
  .sort((a, b) => b.date.localeCompare(a.date));

export const getAllPosts = (): ReadonlyArray<BlogPost> => POSTS;

export const getPostBySlug = (slug: string): BlogPost | undefined => POSTS.find(post => post.slug === slug);

export const getPostsByCategory = (categorySlug: string): BlogPost[] =>
  POSTS.filter(post => post.category === categorySlug);

export const getPostsByCollection = (collectionSlug: string): BlogPost[] =>
  POSTS.filter(post => post.collection === collectionSlug);

/** Case-insensitive tag match, newest first (POSTS is already date-sorted). */
export const getPostsByTag = (tag: string): BlogPost[] => {
  const needle = tag.toLowerCase();
  return POSTS.filter(post => post.tags.some(postTag => postTag.toLowerCase() === needle));
};

/** The index hero: an explicit `featured: true` pin wins, else the newest post. */
export const getFeaturedPost = (): BlogPost | undefined => POSTS.find(post => post.featured) ?? POSTS[0];

/** Same category first (newest), padded with the newest posts from elsewhere. */
export const getRelatedPosts = (current: BlogPost, limit = 3): BlogPost[] => {
  const sameCategory = POSTS.filter(post => post.slug !== current.slug && post.category === current.category);
  const others = POSTS.filter(post => post.slug !== current.slug && post.category !== current.category);
  return [...sameCategory, ...others].slice(0, limit);
};

export const formatPostDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export const formatPostDateline = (post: Pick<BlogPost, "date" | "updated">): string => {
  if (post.updated && post.updated !== post.date) {
    return `Updated ${formatPostDate(post.updated)}`;
  }

  return formatPostDate(post.date);
};
